import React, { useEffect } from "react";

import { Modal, Input, Form, Button, InputNumber, Switch, Row, Col } from "antd";

const { TextArea } = Input;

import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { Locale, Util } from "../../../utils";
import TranslateBtn from "../../../components/product/TranslateBtn";
import LinkSelector from "../../../components/linkSelector/LinkSelector";
import ImageUpload from "../../../components/media/ImageUpload";
import VideoUpload from "../../../components/media/VideoUpload";

const i18n = Locale.getInstance();
const util = Util.getInstance();

const defaultVideoList = (v) => {
  if (!v) return [];
  if (typeof v === "string" && v) {
    return [
      {
        uid: -1,
        name: `video-${Date.now()}`,
        status: "done",
        url: v,
      },
    ];
  }
  if (typeof v === "object" && v.url) {
    return [
      {
        uid: -1,
        name: `video-${Date.now()}`,
        status: "done",
        url: v.url,
        file_path: v.file_path,
        path: v.path,
      },
    ];
  }
  return [];
};

const EditAdForm = ({ adData, onCancel, onFinish, loading, showAdHeight = false }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (adData && Object.keys(adData).length > 0) {
      const {
        zh_title,
        ug_title,
        zh_description,
        ug_description,
        order,
        zh_image,
        ug_image,
        zh_video,
        ug_video,
        zh_video_media,
        ug_video_media,
        status,
        is_dark,
        is_video,
        ad_height,
      } = adData;
      const videoEnabled = is_video === 1 || is_video === true;
      form.setFieldsValue({
        zh_title,
        ug_title,
        zh_description: zh_description ?? "",
        ug_description: ug_description ?? "",
        order: !Number.isNaN(Number(order)) ? Number(order) : 0,
        status: status === 1,
        is_dark: is_dark === 1 || is_dark === true,
        is_video: videoEnabled,
        ad_height:
          ad_height != null && ad_height !== "" && !Number.isNaN(Number(ad_height))
            ? Number(ad_height)
            : null,
        zh_image: util.getDefaultImages(zh_image),
        ug_image: util.getDefaultImages(ug_image),
        zh_video: videoEnabled
          ? defaultVideoList(zh_video_media || zh_video)
          : [],
        ug_video: videoEnabled
          ? defaultVideoList(ug_video_media || ug_video)
          : [],
      });
    }
    return () => {
      form.resetFields();
    };
  }, [adData, form]);

  return (
    <Modal
      width={720}
      title={i18n.t("sales.ad.title")}
      open={!!adData}
      onCancel={onCancel}
      footer={null}
      forceRender
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        initialValues={{
          ug_video: [],
          zh_video: [],
          is_video: false,
        }}
      >
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue, setFieldsValue }) => (
            <Form.Item
              shouldUpdate
              name="ug_title"
              label={i18n.t("agentLevel.ug")}
              initialValue={adData?.ug_title}
              rules={[
                {
                  required: true,
                  message: i18n.t("productEdit.standardRequired"),
                },
              ]}
            >
              <Input
                addonAfter={
                  <TranslateBtn
                    context={getFieldValue("ug_title")}
                    onFinish={(value) =>
                      setFieldsValue({
                        zh_title: value,
                      })
                    }
                    from="ug"
                    to="zh"
                  />
                }
              />
            </Form.Item>
          )}
        </Form.Item>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue, setFieldsValue }) => (
            <Form.Item
              name="zh_title"
              label={i18n.t("agentLevel.zh")}
              initialValue={adData?.zh_title}
              rules={[
                {
                  required: true,
                  message: i18n.t("productEdit.standardRequired"),
                },
              ]}
            >
              <Input
                addonAfter={
                  <TranslateBtn
                    context={getFieldValue("zh_title")}
                    onFinish={(value) =>
                      setFieldsValue({
                        ug_title: value,
                      })
                    }
                    from="zh"
                    to="ug"
                  />
                }
              />
            </Form.Item>
          )}
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => (
                <Form.Item
                  name="ug_image"
                  label={i18n.t("sales.ad.ugImg")}
                  rules={[
                    {
                      required: true,
                      message: i18n.t("productEdit.standardRequired"),
                    },
                  ]}
                >
                  <ImageUpload
                    images={getFieldValue("ug_image")}
                    setSelectedImages={(image) => {
                      setFieldsValue({
                        ug_image: image,
                      });
                    }}
                  />
                </Form.Item>
              )}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => (
                <Form.Item
                  name="zh_image"
                  label={i18n.t("sales.ad.zhImg")}
                  rules={[
                    {
                      required: true,
                      message: i18n.t("productEdit.standardRequired"),
                    },
                  ]}
                >
                  <ImageUpload
                    images={getFieldValue("zh_image")}
                    setSelectedImages={(image) => {
                      setFieldsValue({
                        zh_image: image,
                      });
                    }}
                  />
                </Form.Item>
              )}
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="is_video"
          label={i18n.t("sales.ad.isVideo")}
          valuePropName="checked"
        >
          <Switch
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            onChange={(checked) => {
              if (!checked) {
                form.setFieldsValue({ ug_video: [], zh_video: [] });
              }
            }}
          />
        </Form.Item>

        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue, setFieldsValue }) =>
            getFieldValue("is_video") ? (
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="ug_video"
                    label={i18n.t("sales.ad.ugVideo")}
                  >
                    <VideoUpload
                      video={getFieldValue("ug_video")}
                      setSelectedVideo={(vid) => {
                        setFieldsValue({ ug_video: vid });
                      }}
                      setDeleted={() => {
                        setFieldsValue({ ug_video: [] });
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="zh_video"
                    label={i18n.t("sales.ad.zhVideo")}
                  >
                    <VideoUpload
                      video={getFieldValue("zh_video")}
                      setSelectedVideo={(vid) => {
                        setFieldsValue({ zh_video: vid });
                      }}
                      setDeleted={() => {
                        setFieldsValue({ zh_video: [] });
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            ) : null
          }
        </Form.Item>

        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue, setFieldsValue }) => (
            <Form.Item label={i18n.t("sales.ad.ugDescription")}>
              <div>
                <Form.Item name="ug_description" noStyle>
                  <TextArea rows={2} />
                </Form.Item>
                <div style={{ marginTop: 4, textAlign: "right" }}>
                  <TranslateBtn
                    context={getFieldValue("ug_description")}
                    onFinish={(value) =>
                      setFieldsValue({
                        zh_description: value,
                      })
                    }
                    from="ug"
                    to="zh"
                  />
                </div>
              </div>
            </Form.Item>
          )}
        </Form.Item>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue, setFieldsValue }) => (
            <Form.Item label={i18n.t("sales.ad.zhDescription")}>
              <div>
                <Form.Item name="zh_description" noStyle>
                  <TextArea rows={2} />
                </Form.Item>
                <div style={{ marginTop: 4, textAlign: "right" }}>
                  <TranslateBtn
                    context={getFieldValue("zh_description")}
                    onFinish={(value) =>
                      setFieldsValue({
                        ug_description: value,
                      })
                    }
                    from="zh"
                    to="ug"
                  />
                </div>
              </div>
            </Form.Item>
          )}
        </Form.Item>

        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue, setFieldsValue }) => (
            <Form.Item name="data" shouldUpdate label={i18n.t("sales.ad.type")}>
              <LinkSelector
                selectedLink={adData?.link_data ?? false}
                setSelectedLink={(link) => {
                  setFieldsValue({
                    data: link,
                  });
                }}
              />
            </Form.Item>
          )}
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label={i18n.t("sales.ad.order")}
              name="order"
            >
              <InputNumber
                style={{ width: "100%" }}
                max={99999}
                placeholder={i18n.t("sales.ad.orderPlace")}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="status"
              label={i18n.t("sales.status")}
              valuePropName="checked"
            >
              <Switch
                checkedChildren={<CheckOutlined />}
                unCheckedChildren={<CloseOutlined />}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="is_dark"
              label={i18n.t("sales.ad.isDark")}
              valuePropName="checked"
            >
              <Switch
                checkedChildren={<CheckOutlined />}
                unCheckedChildren={<CloseOutlined />}
              />
            </Form.Item>
          </Col>
        </Row>
        {showAdHeight ? (
          <Form.Item
            label={i18n.t("sales.ad.adHeight")}
            name="ad_height"
            extra={i18n.t("sales.ad.adHeightExtra")}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={1}
              max={100}
              placeholder={i18n.t("sales.ad.adHeightPlace")}
            />
          </Form.Item>
        ) : null}
        <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
          <Button
            disabled={loading}
            loading={loading}
            type="primary"
            htmlType="submit"
          >
            {i18n.t("sales.submit")}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditAdForm;
